import { createColumnHelper } from "@tanstack/react-table";
import { EditIcon, LinkArrow } from "assets/icons";
import { If } from "components";
import { getFormattedUTCDate } from "helpers";
import { useBoolean } from "hooks";
import { DirectUploadMedia } from "libs/directUpload";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Album, Button, Modal, TableItemActionsControl } from "ui";

import {
  ExpenseMediaTypes,
  InternalBankHistoryItemEntity
} from "../../constants";
import { InternalBankHistoryItem } from "../../financial.types";
import { ExpenseModal, ReplenishInternalBankModal } from "../modals";

import styles from "./InternalBankHistoryTable.module.scss";

const columnHelper = createColumnHelper<InternalBankHistoryItem>();

export const useInternalBankHistoryTableColumns = () => {
  const { t } = useTranslation();

  const [proofOfPaymentImages, setProofOfPaymentImages] = useState<
    DirectUploadMedia[] | null
  >([]);

  const [proofOfExpenseImages, setProofOfExpenseImages] = useState<
    DirectUploadMedia[] | null
  >([]);

  const [openedItemOptionsIndex, setOpenedItemOptionsIndex] = useState<
    number | null
  >(null);

  const {
    value: isProofOfPaymentImagesModalOpen,
    setTrue: onOpenProofOfPaymentImagesModal,
    setFalse: onCloseProofOfPaymentImagesModal
  } = useBoolean();

  const {
    value: isProofOfExpenseImagesModalOpen,
    setTrue: onOpenProofOfExpenseImagesModal,
    setFalse: onCloseProofOfExpenseImagesModal
  } = useBoolean();

  const [editableItem, setEditableItem] =
    useState<InternalBankHistoryItem | null>(null);

  const columns = useMemo(
    () => [
      columnHelper.accessor(
        (row) =>
          row.entity === InternalBankHistoryItemEntity.Expense
            ? row.title
            : "Bank Replenishment",
        {
          id: "title",
          header: t("Title & Description"),
          enableSorting: false,
          size: 50,
          minSize: 50,
          cell: (cell) => (
            <>
              <p className={styles.expenses_title}>{cell.getValue()}</p>
              <p className={styles.expenses_description}>
                {cell.row.original.entity ===
                InternalBankHistoryItemEntity.Expense
                  ? cell.row.original.description
                  : ""}
              </p>
            </>
          )
        }
      ),
      columnHelper.accessor(
        (row) =>
          row.entity === InternalBankHistoryItemEntity.Expense
            ? row.quantity
            : "-",
        {
          id: "Quantity",
          header: t("Quantity"),
          size: 3,
          minSize: 3,
          cell: (cell) => cell.getValue()
        }
      ),
      columnHelper.accessor((row) => row.amount, {
        id: "Amount",
        header: `${t("Amount")} (FCFA)`,
        size: 3,
        minSize: 3,
        cell: (cell) => cell.getValue()
      }),
      columnHelper.accessor(
        (row) =>
          row.entity === InternalBankHistoryItemEntity.Expense ? row.type : "-",
        {
          id: "Type",
          header: t("Type"),
          size: 10,
          minSize: 10,
          cell: (cell) => cell.getValue()
        }
      ),
      columnHelper.accessor(
        (row) =>
          row.entity === InternalBankHistoryItemEntity.Expense
            ? row.responsiblePersonName
            : "-",
        {
          id: "responsiblePersonName",
          header: t("Person name"),
          enableSorting: false,
          size: 10,
          minSize: 10,
          cell: (cell) => cell.getValue()
        }
      ),
      columnHelper.accessor(
        (row) =>
          row.entity === InternalBankHistoryItemEntity.Expense
            ? row.paymentMethod
            : undefined,
        {
          id: "paymentMethod",
          header: t("Means of Payment"),
          enableSorting: false,
          size: 11,
          minSize: 11,
          cell: (cell) => {
            if (
              cell.row.original.entity !== InternalBankHistoryItemEntity.Expense
            ) {
              return "-";
            }

            const media = cell.row.original.media;

            const proofOfPaymentMedias = media
              ?.filter((item) => item.type === ExpenseMediaTypes.ProofOfPayment)
              ?.map((item) => item.media);

            return (
              <>
                <p>
                  {t(
                    cell.row.original.paymentMethod.replace(
                      /([a-z])([A-Z])/g,
                      "$1 $2"
                    )
                  )}
                </p>
                {proofOfPaymentMedias && proofOfPaymentMedias.length > 0 && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      if (
                        cell.row.original.entity ===
                        InternalBankHistoryItemEntity.Expense
                      ) {
                        setProofOfPaymentImages(proofOfPaymentMedias);
                      }
                      onOpenProofOfPaymentImagesModal();
                    }}
                    className={styles["expenses_proof-link"]}
                  >
                    {t("Proofs")} <LinkArrow />
                  </div>
                )}
              </>
            );
          }
        }
      ),
      columnHelper.accessor((row) => row.createdAt, {
        id: "CreatedAt",
        header: t("Date"),
        size: 8,
        minSize: 8,
        cell: (cell) => getFormattedUTCDate(cell.getValue(), "DD/MM/YYYY")
      }),
      columnHelper.accessor((row) => row.id, {
        header: "",
        id: "action",
        enableSorting: false,
        size: 7,
        minSize: 7,
        cell: (cell) => {
          if (
            cell.row.original.entity !== InternalBankHistoryItemEntity.Expense
          ) {
            return null;
          }

          const media = cell.row.original.media;

          const proofsOfExpenseMedias = media
            ?.filter((item) => item.type === ExpenseMediaTypes.Proof)
            .map((item) => item.media);

          return cell.row.original.entity ===
            InternalBankHistoryItemEntity.Expense &&
            proofsOfExpenseMedias &&
            proofsOfExpenseMedias.length > 0 ? (
            <Button
              variant="default"
              icon={<LinkArrow width={20} height={20} />}
              onClick={(e) => {
                e.stopPropagation();
                if (
                  cell.row.original.entity ===
                  InternalBankHistoryItemEntity.Expense
                ) {
                  setProofOfExpenseImages(proofsOfExpenseMedias);
                }
                onOpenProofOfExpenseImagesModal();
              }}
            >
              <span
                className={styles["expenses_proof-link"]}
              >{`${t("Proofs")}`}</span>
            </Button>
          ) : null;
        }
      }),

      columnHelper.accessor((row) => row.id, {
        header: "",
        id: "actions",
        enableSorting: false,
        size: 3,
        minSize: 3,
        cell: (cell) => (
          <TableItemActionsControl
            actionsList={[
              {
                label: t("Edit"),
                Icon: <EditIcon fill={"#090914"} width={18} height={18} />,
                onClick: () => {
                  setEditableItem(cell.row.original);
                }
              }
            ]}
            rowIndex={cell.row.index}
            setOpenedItemOptionsIndex={setOpenedItemOptionsIndex}
            openedItemOptionsIndex={openedItemOptionsIndex}
          />
        )
      })
    ],
    [onOpenProofOfPaymentImagesModal, openedItemOptionsIndex, t]
  );

  const proofOfPaymentImagesModal = proofOfPaymentImages ? (
    <Modal
      isOpen={isProofOfPaymentImagesModalOpen}
      onClose={onCloseProofOfPaymentImagesModal}
      title="Means of payment"
      className={styles.proofImagesModal}
      footer={<></>}
    >
      <Album mediaList={proofOfPaymentImages} />
    </Modal>
  ) : null;

  const proofOfExpenseImagesModal = proofOfExpenseImages ? (
    <Modal
      isOpen={isProofOfExpenseImagesModalOpen}
      onClose={onCloseProofOfExpenseImagesModal}
      title="Proof of expense"
      className={styles.proofImagesModal}
      footer={<></>}
    >
      <Album mediaList={proofOfExpenseImages} />
    </Modal>
  ) : null;

  const editModal = (
    <If condition={!!editableItem}>
      {editableItem?.entity === InternalBankHistoryItemEntity.Expense ? (
        <ExpenseModal
          isOpen={!!editableItem}
          onClose={() => setEditableItem(null)}
          editableItem={editableItem}
        />
      ) : (
        <ReplenishInternalBankModal
          isOpen={!!editableItem}
          onClose={() => setEditableItem(null)}
          editableItem={editableItem}
        />
      )}
    </If>
  );

  return {
    columns,
    proofOfPaymentImagesModal,
    proofOfExpenseImagesModal,
    editModal
  };
};
